import type { AddonId } from "../../data/addons";
import type {
  AudioDocFormValues,
  BookmarksFormValues,
  ChatbotFormValues,
  ExperienceLinkType,
  FaqsFormValues,
  QuickRecallFormValues,
  GeneralSettingsFormValues,
  HeyGeniusFormValues,
  KeyPointsFormValues,
  NotesFormValues,
  PodcastFormValues,
  QuizFormValues,
  StandoutsFormValues,
  TriviaFormValues,
  VideoConfigValue,
} from "./types";

/**
 * Starting values for each add-on's configuration card. Central so the draft
 * seeding, the create payload and the cards themselves share one source of
 * truth (importing a component just to read a constant would drag the whole
 * card in). Register a new add-on's defaults here as its card lands.
 *
 * `generation_settings` / `functional_settings` values are API units
 * (fractions, seconds, backend enums) — see `./types.ts`.
 *
 * TODO(MED-6810): these are placeholders until the backend exposes real
 * per-add-on defaults/estimates.
 */

// Static, card-level fallbacks only. Video's other fields resolve from the
// org-preference defaults (`useVideoConfigData`) or the first available option,
// so they don't get a static default here.
export const VIDEO_DEFAULTS = {
  generation_settings: {
    duration: 120,
    aspect_ratio: "desktop",
    auto_select_music: false,
    with_avatar: false,
    generate_brandkit: true,
  },
  // Hidden for now — waiting for backend confirmation that subtitles are
  // supported. Re-enable with the card's toggle.
  // subtitles: true,
} satisfies {
  generation_settings: Partial<VideoConfigValue["generation_settings"]>;
};

// Mirrors VIDEO_DEFAULTS: same generation_settings shape, avatar always on
// since HeyGenius IS the on-screen-presenter experience.
export const HEYGENIUS_DEFAULTS: HeyGeniusFormValues = {
  generation_settings: {
    duration: 120,
    with_avatar: true,
    auto_select_music: false,
  },
  subtitles: true,
  positionOnDocument: "bottomRight",
  openingGreeting: "",
};

export const AUDIODOC_DEFAULTS: AudioDocFormValues = {
  generation_settings: {
    with_emotions: true,
    skip_elements: true,
  },
  functional_settings: {
    reading_speed: "Normal",
  },
  // TODO: not in the AudioDoc API contract — restore if the backend adds
  // voice mood / language / intonation fields.
  // voice: "warm",
  // language: "en-US",
  // intonation: "Natural",
};

/**
 * Speaker roles carried by the podcast's `voice_profile_ids`, in order. The
 * backend requires EXACTLY two profiles: [0] speaks the Host lines, [1] the
 * Guest lines (see `PodcastGenerationSettings`).
 */
export const PODCAST_SPEAKER_ROLES = ["host", "guest"] as const;

export type PodcastSpeakerRole = (typeof PODCAST_SPEAKER_ROLES)[number];

export const PODCAST_DEFAULTS: PodcastFormValues = {
  // `background_music` was removed from the card — omitted from the seed so
  // the backend default applies. `target_duration_in_s` is deliberately not
  // seeded either: unset means "Auto" (the backend's configured default).
  // Voices are injected by `seedAddonSettings`.
  generation_settings: {},
  functional_settings: {
    playback_speed_control: true,
    auto_play_on_open: false,
  },
  // TODO: not in the Podcast API contract — restore if the backend adds
  // format / voice mood / language fields.
  // format: "podcast",
  // voice: "warm",
  // language: "en-US",
};

export const QUICK_RECALL_DEFAULTS: QuickRecallFormValues = {
  generation_settings: {
    nb_of_cards: 10,
  },
  functional_settings: {
    card_order: "Sequential",
  },
  // Hidden for now — re-enable together with their form items in
  // QuickRecallConfigCard.
  // answerFormat: "shortAnswer",
  // showHints: true,
  // reviewSummary: true,
};

// General settings starts with no experience links — the user adds them (or
// they arrive via a draft/backend value); nothing is seeded here. Brand kit /
// avatar defaults come from the org preferences (`useGeneralConfigData`), not
// statically.
export const GENERAL_SETTINGS_DEFAULTS: GeneralSettingsFormValues = {
  name: "",
  tags: [],
  description: "",
  links: [],
  generation_settings: {
    language: "auto",
  },
  functional_settings: {
    side_panel_position: "Right",
  },
};

/** Type a freshly added experience link starts as (the user can change it). */
export const NEW_LINK_TYPE: ExperienceLinkType = "link";

export const BOOKMARKS_DEFAULTS: BookmarksFormValues = {
  functional_settings: {
    allow_viewer_bookmarks: true,
    allow_comments: true,
    show_panel: true,
  },
  syncAcrossDevices: true,
};

export const NOTES_DEFAULTS: NotesFormValues = {
  privateToViewer: true,
  enableHighlighting: true,
  allowExport: false,
};

export const TRIVIA_DEFAULTS: TriviaFormValues = {
  generation_settings: {
    nb_of_questions: 10,
    difficulty: "Mixed",
  },
  functional_settings: {
    // -1 = no time limit.
    time_per_question: -1,
    question_order: "Random",
  },
  enableGroupLeaderboard: true,
};

export const FAQS_DEFAULTS: FaqsFormValues = {
  numberOfFaqs: "auto",
  autoGenerateFromContent: true,
  allowViewerQuestions: false,
};

// Named after the frontend "Key points" add-on, wired to the backend
// `Summary` add-on type — see `KeyPointsFormValues`.
export const KEY_POINTS_DEFAULTS: KeyPointsFormValues = {
  generation_settings: {
    length: "Medium",
    format: "Paragraphs",
    reading_level: "Standard",
    include_key_take_aways: true,
    separate_into_chapters: true,
  },
  functional_settings: {},
};

// Backend default for `nb_of_standouts` is 10 (see `StandoutsGenerationSettings`).
export const STANDOUTS_DEFAULTS: StandoutsFormValues = {
  generation_settings: {
    nb_of_standouts: 10,
  },
  functional_settings: {},
};

export const QUIZ_DEFAULTS: QuizFormValues = {
  generation_settings: {
    // null = Automatic: the API derives the difficulty from the creator DNA.
    difficulty: null,
  },
  functional_settings: {
    passing_score: 0.7,
    nb_of_questions_per_subset: 10,
  },
  // Hidden while grading is out of scope — re-enable with their form items if
  // grading is going to be taken into account for this add-on.
  // maxAttemptsPerViewer: 3,
  // timesEachQuestionIsShown: 1,
  // Hidden together with the "randomize questions" toggle.
  // showImprovementTipsAtEnd: true,
};

// Mirrors the `ChatbotFunctionalSettings` schema defaults, so the card shows
// what the backend would apply on its own. The greeting starts empty — the
// textarea's placeholder previews the wording the viewer sees instead.
export const CHATBOT_DEFAULTS: ChatbotFormValues = {
  functional_settings: {
    answer_scope: "Grounded",
    tone: "Friendly",
    greeting_message: "",
    show_suggested_questions: true,
    show_sources: true,
  },
};

/**
 * Card defaults keyed by backend add-on id. The draft is seeded with these the
 * moment an add-on is added, so an untouched add-on still transmits the exact
 * settings its card displayed (rather than falling back to different backend
 * schema defaults). UI-only add-ons (document/notes/faqs/heygenius/forms/
 * esignature) and cardless ones (Chatbot / Standouts) are absent — they seed empty and
 * rely on backend defaults.
 */
export const ADDON_DEFAULTS: Partial<Record<AddonId, object>> = {
  Video: VIDEO_DEFAULTS,
  Podcast: PODCAST_DEFAULTS,
  AudioDoc: AUDIODOC_DEFAULTS,
  Quiz: QUIZ_DEFAULTS,
  Trivia: TRIVIA_DEFAULTS,
  Flashcards: QUICK_RECALL_DEFAULTS,
  Bookmarks: BOOKMARKS_DEFAULTS,
  Chatbot: CHATBOT_DEFAULTS,
  Summary: KEY_POINTS_DEFAULTS,
  Standouts: STANDOUTS_DEFAULTS,
};
